/**
 * #981 stage 2 — the large-CSV grid's editing toolbar (spec §8.3, §8.5): Save / Discard for the
 * whole file, "Saving…" while a save streams it (editing off), the conflict choices (overwrite,
 * discard, save as a copy), and the last answer that needs saying (a retry, a refusal, an edit
 * that could not be applied). Rendered only for a person who may edit.
 */
import { useEffect, useState } from 'react'
import { Loader2, AlertTriangle } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import type { LargeCsvSessionApi } from '../../client/useLargeCsvSession.js'

export interface LargeCsvEditBarProps {
  session: LargeCsvSessionApi
  /** A message from the grid itself (an edit refused), shown until the next one. */
  message: string | null
  /** Save a copy of the current edits (conflict). */
  onSaveCopy: () => void
  /** Retry the last copy (a retryable answer). */
  onRetryCopy: () => void
  copyBusy: boolean
  /** The last copy answer to show (and offer Retry for). */
  copyMessage: { text: string; retry: boolean } | null
  /** Re-open the session (after it was disconnected for good). */
  onReopen: () => void
  /**
   * The grid is switching in place (#981): to a save's content (`save` — still "Saving…" until the rows on screen
   * are the saved ones) or back to the same content after a Discard / failed save (`reset` — "Updating…"). Editing
   * is off meanwhile, and says so.
   */
  swapping?: 'save' | 'reset' | null
}

export function LargeCsvEditBar({ session, message, onSaveCopy, onRetryCopy, copyBusy, copyMessage, onReopen, swapping = null }: LargeCsvEditBarProps) {
  const { t } = useTranslation('collab')
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const { dirty, savePending, conflicted, notice, ended, ready } = session
  const saving = session.saving || swapping === 'save'

  useEffect(() => { if (!dirty || saving) setConfirmDiscard(false) }, [dirty, saving])

  const noticeText = (() => {
    if (!notice) return null
    switch (notice.kind) {
      case 'retry': return notice.reason === 'save_in_progress' ? t('largeCsv.edit.saveInProgress') : t('largeCsv.edit.retry')
      case 'refused': return notice.message
      case 'failed': return t('largeCsv.edit.saveFailed')
      case 'discard_failed':
        return notice.reason === 'save_in_progress' ? t('largeCsv.edit.discardFailed.saving')
          : notice.reason === 'unknown_base' ? t('largeCsv.edit.discardFailed.gone')
            : notice.reason === 'forbidden' ? t('largeCsv.edit.discardFailed.forbidden')
              : t('largeCsv.edit.discardFailed.error')
      case 'edits_lost': return t('largeCsv.edit.editsLost')
      case 'not_synced': return t('largeCsv.edit.notSynced')
      case 'save_refused':
        return notice.reason === 'unencodable' ? t('largeCsv.edit.unencodable', { encoding: notice.encoding ?? '' })
          : notice.reason === 'layout_changed' ? t('largeCsv.edit.layoutChanged')
            : t('largeCsv.edit.saveFailed')
    }
  })()
  // too large to edit here: Save / Discard / copies still act, so their outcome shows over the ended text (#981 Task 13)
  const shown = ended === 'too_large' ? copyMessage?.text ?? noticeText ?? t('largeCsv.edit.ended.too_large')
    : ended ? t(`largeCsv.edit.ended.${ended}`) : copyMessage?.text ?? noticeText ?? message
  // Save / Discard / the conflict's choices act on the edits kept so far — also once a fragment was too large to edit
  // here (#981 Task 13: the server saves and discards without that fragment's room; only editing stops)
  const canAct = ready && !saving && (!ended || ended === 'too_large') && !swapping

  return (
    <div className="flex items-center gap-2 shrink-0" data-testid="large-csv-edit-bar">
      {saving && (
        <span className="text-xs themed-text-muted whitespace-nowrap inline-flex items-center gap-1" role="status" data-testid="large-csv-saving">
          <Loader2 className="animate-spin" size={12} />
          {t('largeCsv.edit.saving')}
        </span>
      )}
      {savePending && !saving && session.saveWaitsForEdits && (
        <span className="text-xs themed-text-muted whitespace-nowrap inline-flex items-center gap-1" role="status" data-testid="large-csv-save-waiting">
          <Loader2 className="animate-spin" size={12} />
          {t('largeCsv.edit.saveWaitsForEdits')}
        </span>
      )}
      {swapping === 'reset' && (
        <span className="text-xs themed-text-muted whitespace-nowrap inline-flex items-center gap-1" role="status" data-testid="large-csv-updating">
          <Loader2 className="animate-spin" size={12} />
          {t('largeCsv.edit.updating')}
        </span>
      )}
      {conflicted && !saving && (
        <span className="text-xs text-amber-500 whitespace-nowrap inline-flex items-center gap-1" role="alert" data-testid="large-csv-conflict">
          <AlertTriangle size={12} />
          {t('largeCsv.edit.conflict')}
          <button className="px-2 py-0.5 rounded themed-btn-secondary" disabled={!canAct || savePending} onClick={session.forceSave} data-testid="large-csv-overwrite">
            {t('largeCsv.edit.overwrite')}
          </button>
          <button className="px-2 py-0.5 rounded themed-btn-secondary" disabled={!canAct || copyBusy} onClick={onSaveCopy} data-testid="large-csv-save-copy">
            {t('largeCsv.edit.saveCopy')}
          </button>
        </span>
      )}
      {shown && (
        <span className="text-xs themed-text-muted max-w-[22rem] truncate" role="status" title={shown} data-testid="large-csv-edit-notice">
          {shown}
          {ended === 'disconnected' && (
            <button className="ml-1 underline" onClick={onReopen} data-testid="large-csv-reopen">{t('largeCsv.edit.reopen')}</button>
          )}
          {!ended && copyMessage?.retry && (
            <button className="ml-1 underline" onClick={onRetryCopy} disabled={copyBusy}>{t('largeCsv.retry')}</button>
          )}
        </span>
      )}
      {dirty && !saving && !conflicted && (
        <span className="text-xs themed-text-muted whitespace-nowrap" data-testid="large-csv-unsaved">{t('largeCsv.edit.unsaved')}</span>
      )}
      <button
        className="px-2 py-0.5 text-xs rounded themed-btn-secondary disabled:opacity-40"
        disabled={!canAct || !dirty || !!session.discardPending}
        onClick={() => { if (confirmDiscard) { setConfirmDiscard(false); session.discard() } else setConfirmDiscard(true) }}
        data-testid="large-csv-discard"
      >
        {confirmDiscard ? t('largeCsv.edit.confirmDiscard') : t('largeCsv.edit.discard')}
      </button>
      <button
        className="px-2 py-0.5 text-xs rounded themed-btn-primary disabled:opacity-40"
        disabled={!canAct || !dirty || savePending}
        onClick={session.save}
        data-testid="large-csv-save"
      >
        {savePending ? <Loader2 className="animate-spin inline" size={12} /> : t('largeCsv.edit.save')}
      </button>
    </div>
  )
}
